import React, { useCallback, useContext } from 'react'
import { NavLink, useRouteMatch } from 'react-router-dom'

import { HeaderMenu } from './menu'
import { WashesMenu } from './washes-menu'
import { WhatsAppGlyph } from './glyphs'
import { NAV_LINKS, WHATSAPP_MESSAGE } from './nav'
import { image } from '../../lib/image'
import { whatsappChatProps } from '../whatsapp-chat'
import { bookingPath, enterBooking } from '../../lib/booking-entry'
import { context, useSelectors, userSignOut } from '../../store'

import './header.css'

// PAGE CHROME - THE HEADER. Design direction §2.1.1 (1440) and §2.2.1 (390).
// Read client/gwmc/DESIGN.md first; the reasoning for every measurement is in
// ./header.css next to the rule it explains.
//
// WHAT WAS WRONG, and what each fix is:
//
//   1. The WhatsApp button was the loudest thing on the page: a full
//      saturation green pill, white 600 text, a green glow, beside a quiet
//      serif mark and grey nav text. DESIGN.md §3 and the palette table say
//      --whatsapp is "the WhatsApp glyph only. Not a button fill, not a FAB",
//      so the header was breaking the contract it exists to follow. It is now
//      a text control the weight of a nav link with the glyph in green. The
//      argument is in header.css above .gwmc-header-wa.
//   2. The mark did not line up. Horizontally it sat in 70px of hard-coded
//      padding while the page beneath it uses .gwmc-container, so at 1440 it
//      was 74px to the left of the H1 under it. Vertically its baseline sat
//      4px below the nav's, because the artwork is cropped to its ink and
//      centring the box centres the descender with it. Both are fixed in
//      header.css; it is also 24px drawn rather than 32.
//   3. The nav held one destination. It holds two now, and why those two and
//      not an areas link or a booking button is in ./nav.js.
//
// NO MUI. This component imported AppBar, Toolbar, Box, Hidden, makeStyles and
// two icon packages, and the menu imported Menu, MenuItem and IconButton on
// top. All of it is gone: a <header>, the shared .gwmc-container, a stylesheet
// and two inline paths. §3 gives the home chunk 250 KB gzipped and the header
// is inside it. MUI is still in the bundle (app.js mounts Dialog, Snackbar and
// CssBaseline), so this does not remove the dependency, but the header no
// longer adds Popover, Modal, Transition or the icon modules to the fold.
//
// `Hidden` is gone with it, which matters for more than bytes: `Hidden`
// decides at runtime from a media query, and the server-rendered copy of this
// bar in app/views/gwmc/_hero.html.haml cannot run it. One DOM at both widths,
// switched by one media query at 960, is the only version the server can
// paint identically.
//
// WHAT THE SERVER MUST PAINT. app/views/gwmc/_hero.html.haml renders this same
// bar before the bundle runs. If the two disagree the header jumps on mount.
// The classes below are the contract; the mark's width and height attributes
// have to match MARK, and the drawn size comes from --mark-h in header.css for
// both.

// THE MARK, in one place. 4802 x 945 artwork, so 122 x 24 at the drawn height
// --mark-h gives it. The attributes exist to reserve the box before the bytes
// land (DESIGN.md §3: every <img> carries width and height); header.css owns
// what it is actually drawn at. A replacement mark changes this pair, the same
// pair in _hero.html.haml, and --mark-h if the new one wants a different
// height. Nothing else measures it.
const MARK = { width: 137, height: 24 }

export const Header = React.memo(() => {
  const { dispatch } = useContext(context)
  const { isUserSignedIn } = useSelectors()
  const handleSignOut = useCallback(() => dispatch(userSignOut()), [dispatch])

  const isBookingFinishPage = !!useRouteMatch('/booking/finish')
  const isBookingPage = !!useRouteMatch('/booking/:step') && !isBookingFinishPage

  // The booking screen is one screen and owns its own chrome
  // (docs/ONE_SCREEN_CHECKOUT_SPEC.md).
  if (isBookingPage) return null

  return (
    <header className="gwmc-header">
      <div className="gwmc-container gwmc-header-bar">
        {/* `exact`, or the mark carries aria-current="page" on every route. */}
        <NavLink exact className="gwmc-header-brand" to="/" aria-label="GoWashMyCar, home">
          <img
            className="gwmc-header-mark"
            src={image('./wordmark.svg')}
            alt="GoWashMyCar"
            width={MARK.width}
            height={MARK.height}
          />
        </NavLink>

        <div className="gwmc-header-actions">
          <nav className="gwmc-header-nav" aria-label="Main">
            <WashesMenu />
            {NAV_LINKS.map(link => (
              <NavLink key={link.to} className="gwmc-header-link" to={link.to}>
                {link.label}
              </NavLink>
            ))}
            {isUserSignedIn && (
              <button type="button" className="gwmc-header-link" onClick={handleSignOut}>
                Sign out
              </button>
            )}
          </nav>

          {/* On a desktop whatsappChatProps swallows the click and opens the
              number form instead, because wa.me there means WhatsApp Web. On a
              phone the wa.me href is left alone. */}
          <a className="gwmc-header-wa" {...whatsappChatProps(WHATSAPP_MESSAGE)}>
            <WhatsAppGlyph />
            <span className="gwmc-header-wa-text">WhatsApp us</span>
          </a>

          {/* The booking, one tap from every page, desktop and phone (copy
              deck, 5 Oct). Mirrored in app/views/gwmc/_hero.html.haml. */}
          <a
            className="gwmc-btn gwmc-btn--primary gwmc-header-book"
            href={bookingPath()}
            onClick={event => {
              event.preventDefault()
              enterBooking(null, 'header')
            }}>
            Book now
          </a>

          <HeaderMenu />
        </div>
      </div>
    </header>
  )
})
